import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '@/shared/api';
import config from '@/config';
import MultiSelect, {
  removeItem as removeMultiSelectItem,
  addItem as addMultiSelectItem,
  splitItems as splitMultiSelectItems,
  itemEquals,
} from '@/shared/components/input/multiselect/MultiSelect';
import AuthService from '@/shared/services/AuthService';

const InteractionTagMultiSelect = ({
  value,
  onChange,
  placeholder,
  allowNew = false,
  idProp,
  caseSensitive = true,
  disabled = false,
}) => {
  const [items, setItems] = useState(null);
  const canCreateTags = useMemo(
    () => AuthService.hasPermission({ functionality: 'Tags', privilege: 'Create' }),
    [],
  );

  useEffect(() => {
    api.base.get(`${config.appUrls.interactions}/api/tags`).then(({ data }) => {
      const newItems = (data || []).map(x => ({
        ...x,
        label: x.name,
      }));
      setItems(newItems);
    });
  }, []);

  const onCreateItem = inputValue => {
    if (!inputValue || !allowNew || !canCreateTags) return;
    if (items.some(x => itemEquals(x.label, inputValue, caseSensitive))) return;

    const item = {
      // Id will be generated in backend when saved
      name: inputValue,
      label: inputValue,
    };
    onChange(addMultiSelectItem(value, item, idProp, caseSensitive));
    setItems([...items, item]);
  };

  if (!items) return null;

  const [selectedItems, availableItems] = splitMultiSelectItems(
    items,
    value,
    idProp,
    caseSensitive,
  );

  return (
    <MultiSelect
      selectedItem={selectedItems}
      onItemRemoved={item => onChange(removeMultiSelectItem(value, item, idProp, caseSensitive))}
      onItemSelected={item => onChange(addMultiSelectItem(value, item, idProp, caseSensitive))}
      items={availableItems}
      placeholder={placeholder}
      onCreateItem={allowNew && canCreateTags ? onCreateItem : undefined}
      disabled={disabled}
    />
  );
};
InteractionTagMultiSelect.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  allowNew: PropTypes.bool,
  caseSensitive: PropTypes.bool,
  disabled: PropTypes.bool,
  idProp: PropTypes.string,
};

InteractionTagMultiSelect.defaultProps = {
  value: [],
  placeholder: undefined,
  allowNew: false,
  caseSensitive: true,
  disabled: false,
  idProp: 'id',
};

export default InteractionTagMultiSelect;
